const array = new Uint32Array(10)

/**
 * Generate a random nonce.
 */
export function nonce() {
  crypto.getRandomValues(array)

  return Buffer.from(array).toString('base64')
}

/**
 * Produces a hash the text using sha256 and encoded in base64.
 */
export async function hash(text: string) {
  const encoder = new TextEncoder()
  const data = encoder.encode(text)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)

  // Convert the hash buffer directly to Base64
  return Buffer.from(hashBuffer).toString('base64')
}

/**
 * Decodes a JSON Web Token (JWT) and returns its payload as a parsed object.
 *
 * @template T - The expected type of the JWT payload. Defaults to Record<string, unknown>
 * @param {string} token - The JWT string to decode
 * @returns {T | null} The decoded payload as type T, or null if decoding fails
 *
 * @remarks
 * This function only decodes the JWT payload without verifying the signature.
 * It performs Base64 decoding and JSON parsing of the payload section.
 */
export function decodeJWT<T extends object = Record<string, unknown>>(token: string): T | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) throw new Error('Invalid JWT format')

    const payload = parts[1]

    if (!payload) throw new Error('Invalid JWT payload')

    const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/')) // Base64 decoding
    const parsed = JSON.parse(decodedPayload)

    return typeof parsed === 'object' && parsed !== null ? parsed : null
  } catch (error) {
    console.error('Error decoding JWT:', error)

    return null
  }
}

/**
 * Generates a random numeric ID of the specified length.
 */
export function randomNumericId(length = 16): string {
  const digits = new Uint8Array(length)
  crypto.getRandomValues(digits)

  return Array.from(digits, (d) => (d % 10).toString()).join('')
}
